import { useCallback, useMemo } from 'react';
import { useAtomValue, useSetAtom } from 'jotai';
import { ANNOTATION_EVENT_NAMES } from '../../../../../../../analytics/event-name';
import useAnalytics from '../../../../../../../analytics/useAnalytics';
import { ACTION_BY, NOTIFICATION_TYPE } from '../../../../../../../common/constant/constant';
import type { AnnotationLabelT, RangeSpanT, ShowNotificationT } from '../../../../../../../common/types/types';
import { annotationLabelsAtom, annotationMetadataAtom, currentFrameLoadedAnnotationsAtom } from '../../../../../../labelling-v2/atoms/annotation-atoms';
import { dispatchAtom } from '../../../../../../labelling-v2/atoms/label-store-dispatch/dispatch';
import { EVENTS, UNDOABLE_EVENTS } from '../../../../../../labelling-v2/atoms/label-store-dispatch/events';
import { currentFrameAtom } from '../../../../../../labelling-v2/atoms/video-playback-atoms';
import { buildAnnotationForOutput } from '../../../../../../labelling-v2/utils/build-annotation-for-output';
import type { BoundingBoxTrackingInputT, BoundingBoxTrackingOutputT, SAMSegmentVideoData } from '../../../../../../types';
import { ANNOTATION_TYPE } from '../../../../../constants';
import { useSegmentationBitmapUpdater } from '../../../../../providers/hooks/use-segmentation-bitmap-updater';
import { handleAnnotationTracking, handleBoundingBoxTracking } from '../../../../../providers/utils/tracking';
import type { AnnotationT } from '../../../../../types';
import useAnalyticsForVideoLabelling from '../../../analytics/useAnalyticsForVideoLabelling';
import { getNoAnnotationFoundErrorMessage, getTrackingStartFrameErrorMessage } from '../../../utils';
type PropsT = {
  showNotification?: ShowNotificationT;
  getBoundingBoxTrackingData?: (frameIndex: number, bbox: BoundingBoxTrackingInputT['bbox'], numFramesToTrack: number) => Promise<BoundingBoxTrackingOutputT | Error>;
  fetchAnnotationTrackingData?: (labelTitle: string, frameRange?: {
    start: number;
    end: number;
  }) => Promise<SAMSegmentVideoData | Error>;
};
export function useAnnotationTimelineHandlers({
  showNotification,
  getBoundingBoxTrackingData,
  fetchAnnotationTrackingData
}: PropsT) {
  const dispatch = useSetAtom(dispatchAtom);
  const currentFrame = useAtomValue(currentFrameAtom);
  const currentFrameAnnotations = useAtomValue(currentFrameLoadedAnnotationsAtom);
  const annotationMetadata = useAtomValue(annotationMetadataAtom);
  const annotationLabels = useAtomValue(annotationLabelsAtom);
  const {
    updateBitmapsOnTrack
  } = useSegmentationBitmapUpdater();
  const trackingActions = useMemo(() => ({
    setLoading: (isLoading: boolean) => dispatch({
      type: EVENTS.SET_LOADING,
      isLoading
    }),
    updateTrackingFrames: (annotationId: string, startFrame: number, endFrame: number, updatedAnnotationsArray: {
      [frameIndex: number]: AnnotationT;
    }) => dispatch({
      type: EVENTS.UPDATE_TRACKING_ANNOTATION_FRAMES,
      annotationIdx: annotationId,
      startFrame,
      endFrame,
      frameAnnotations: updatedAnnotationsArray
    }),
    setCurrentFrame: (frameId: number) => dispatch({
      type: EVENTS.SET_CURRENT_FRAME,
      frameId
    }),
    updateBitmapsOnTrack: (samTrackingData: SAMSegmentVideoData, annotationLabel: AnnotationLabelT, startFrame: number, sourceAnnotation?: AnnotationT) => updateBitmapsOnTrack(samTrackingData, annotationLabel, startFrame, sourceAnnotation)
  }), [dispatch, updateBitmapsOnTrack]);
  const {
    sendEventVideoSAMTrack
  } = useAnalyticsForVideoLabelling();
  const {
    sendEvent
  } = useAnalytics();
  const handleSegmentationTracking = useCallback(async (annotationId: string, annotationSequence: Map<string, RangeSpanT>) => {
    const trackingStartTime = Date.now();
    const framePayload = currentFrameAnnotations.get(annotationId);
    const meta = annotationMetadata.get(annotationId);
    let annotation: AnnotationT | undefined;
    if (framePayload && meta) {
      annotation = buildAnnotationForOutput(framePayload, meta, annotationLabels, currentFrame);
    }
    if (!annotation) {
      showNotification?.(NOTIFICATION_TYPE.ERROR, getNoAnnotationFoundErrorMessage(currentFrame));
      return;
    }
    const trackedSequence = await handleAnnotationTracking({
      state: {
        annotationId,
        annotation,
        annotationSequence
      },
      actions: trackingActions,
      fetchAnnotationTrackingData,
      showNotification
    });
    if (trackedSequence) {
      const timeTaken = ((Date.now() - trackingStartTime) / 1000).toFixed(2);
      sendEventVideoSAMTrack({
        startFrame: trackedSequence.start,
        endFrame: trackedSequence.end
      });
      sendEvent({
        action: ANNOTATION_EVENT_NAMES.ADD_ANNOTATION,
        payload: {
          objectType: annotation.annotationType,
          label: annotation.annotationLabel.title,
          objectId: annotationId,
          timeTaken,
          actionBy: ACTION_BY.AUTOMATION,
          frame: trackedSequence.start + 1,
          annotation_type: 'ml'
        }
      });
    }
  }, [trackingActions, fetchAnnotationTrackingData, showNotification, sendEventVideoSAMTrack, sendEvent, currentFrameAnnotations, annotationMetadata, annotationLabels, currentFrame]);
  const handleRectangleTracking = useCallback(async (annotationId: string, annotationSequence: Map<string, RangeSpanT>) => {
    const sequenceEntries = Array.from(annotationSequence.values());
    const lastSequence = sequenceEntries[sequenceEntries.length - 1];
    // @ts-expect-error todo(ts-migration) TS18048 'lastSequence' is possibly 'undefined'.
    const currentFrame = lastSequence.start - 1;
    const framePayload = currentFrameAnnotations.get(annotationId);
    const meta = annotationMetadata.get(annotationId);
    let currentFrameAnnotation: AnnotationT | undefined;
    if (framePayload && meta) {
      currentFrameAnnotation = buildAnnotationForOutput(framePayload, meta, annotationLabels, currentFrame);
    }
    if (!currentFrameAnnotation) {
      showNotification?.(NOTIFICATION_TYPE.ERROR, getNoAnnotationFoundErrorMessage(currentFrame));
      return;
    }
    const trackingStartTime = Date.now();
    const trackedRange = await handleBoundingBoxTracking({
      state: {
        annotationId,
        annotation: currentFrameAnnotation,
        annotationSequence
      },
      actions: trackingActions,
      getBoundingBoxTrackingData,
      showNotification
    });
    if (trackedRange) {
      const timeTaken = ((Date.now() - trackingStartTime) / 1000).toFixed(2);
      sendEvent({
        action: ANNOTATION_EVENT_NAMES.ADD_ANNOTATION,
        payload: {
          objectType: currentFrameAnnotation.annotationType,
          label: currentFrameAnnotation.annotationLabel.title,
          objectId: annotationId,
          timeTaken,
          actionBy: ACTION_BY.AUTOMATION,
          frame: trackedRange.start + 1,
          annotation_type: 'ml'
        }
      });
    }
  }, [trackingActions, getBoundingBoxTrackingData, showNotification, sendEvent, currentFrameAnnotations, annotationMetadata, annotationLabels]);
  const handleAnnotationTypeTracking = useCallback(async (annotationId: string, annotationType: AnnotationT['annotationType'], annotationSequence: Map<string, RangeSpanT>) => {
    switch (annotationType) {
      case ANNOTATION_TYPE.SEGMENTATION:
        await handleSegmentationTracking(annotationId, annotationSequence);
        break;
      case ANNOTATION_TYPE.RECTANGLE:
        await handleRectangleTracking(annotationId, annotationSequence);
        break;
      default:
        showNotification?.(NOTIFICATION_TYPE.ERROR, `Tracking not supported for annotation type: ${annotationType}`);
    }
  }, [handleSegmentationTracking, handleRectangleTracking, showNotification]);
  const handleOnChange = useCallback(async (annotationId: string, annotationType: AnnotationT['annotationType'], annotationSequence: Map<string, RangeSpanT>, close, isAnnotationTracking?: boolean) => {
    if (isAnnotationTracking) {
      const sequenceEntries = Array.from(annotationSequence.values());
      const lastSequence = sequenceEntries[sequenceEntries.length - 1];
      const startFrame = annotationMetadata.get(annotationId)?.spans[0]?.start ?? -1;
      // @ts-expect-error todo(ts-migration) TS18048 'lastSequence' is possibly 'undefined'.
      if (lastSequence.start < startFrame) {
        showNotification?.(NOTIFICATION_TYPE.ERROR, getTrackingStartFrameErrorMessage(startFrame));
        return;
      }
      await handleAnnotationTypeTracking(annotationId, annotationType, annotationSequence);
    } else {
      const entries = Array.from(annotationSequence.entries());
      const latestSequence = entries[entries.length - 1];
      if (latestSequence) {
        await dispatch({
          type: UNDOABLE_EVENTS.UPDATE_ALL_ANNOTATION_FRAME_SPAN,
          annotationIdx: annotationId,
          startFrame: latestSequence[1].start,
          endFrame: latestSequence[1].end
        });
      }
    }

    // TODO: add sendEventAnnotationInterpolate
    close();
  }, [handleAnnotationTypeTracking, dispatch, showNotification, annotationMetadata]);
  return {
    handleOnChange
  };
}